import { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UploadCloud, Loader2, FileSpreadsheet, AlertTriangle, CheckCircle2, FileDown } from "lucide-react";
import { gbp } from "@/lib/format";
import { downloadTemplate } from "@/lib/csv";

// entity: "rate-cards" | "cost-of-goods"
export default function UploadDialog({ open, onOpenChange, entity, columns, onDone }) {
  const [stage, setStage] = useState("select"); // select | resolve
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [actions, setActions] = useState({}); // index -> insert|overwrite|skip
  const fileRef = useRef();

  const reset = () => {
    setStage("select");
    setPreview(null);
    setActions({});
    setLoading(false);
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload/${entity}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.total === 0) {
        toast.error("No rows found in the file.");
        setLoading(false);
        return;
      }
      const initial = {};
      data.rows.forEach((r) => {
        initial[r.index] = r.is_duplicate ? "skip" : "insert";
      });
      setActions(initial);
      setPreview(data);
      setStage("resolve");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setAll = (action, onlyDuplicates) => {
    const next = { ...actions };
    preview.rows.forEach((r) => {
      if (onlyDuplicates && !r.is_duplicate) return;
      if (!onlyDuplicates && r.is_duplicate) return;
      next[r.index] = action;
    });
    setActions(next);
  };

  const commit = async () => {
    setLoading(true);
    try {
      const rows = preview.rows.map((r) => ({ incoming: r.incoming, action: actions[r.index] }));
      const { data } = await api.post(`/upload/${entity}/commit`, { rows });
      toast.success(
        `Imported: ${data.inserted} added, ${data.overwritten} overwritten, ${data.skipped} skipped.`
      );
      close(false);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => (typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(2)) : v || "—");

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl" data-testid="upload-dialog">
        {stage === "select" && (
          <>
            <DialogHeader>
              <DialogTitle>Upload {entity === "rate-cards" ? "Rate Cards" : "Cost of Goods"}</DialogTitle>
              <DialogDescription>
                Upload an Excel (.xlsx) or CSV file. We'll check for duplicate records before saving.
              </DialogDescription>
            </DialogHeader>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-10 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-colors"
              data-testid="upload-dropzone"
            >
              {loading ? (
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
              ) : (
                <>
                  <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Click to choose a file</p>
                  <p className="text-xs text-slate-400 mt-1">Expected columns: {columns.join(", ")}</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFile}
                className="hidden"
                data-testid="upload-file-input"
              />
            </div>
            <button
              type="button"
              onClick={() => downloadTemplate(entity, columns)}
              data-testid="upload-download-template"
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              <FileDown className="w-4 h-4" /> Download a blank template
            </button>
          </>
        )}

        {stage === "resolve" && preview && (
          <>
            <DialogHeader>
              <DialogTitle>Review & Resolve Duplicates</DialogTitle>
              <DialogDescription className="flex flex-wrap gap-2 items-center pt-1">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> {preview.new_count} new
                </Badge>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {preview.duplicate_count} duplicate{preview.duplicate_count !== 1 ? "s" : ""}
                </Badge>
                <span className="text-xs text-slate-400">of {preview.total} rows</span>
              </DialogDescription>
            </DialogHeader>

            {preview.duplicate_count > 0 && (
              <div className="flex flex-wrap gap-2 items-center text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-amber-800 font-medium">Bulk actions for duplicates:</span>
                <Button size="sm" variant="outline" className="h-7" data-testid="duplicate-modal-overwrite-all-button" onClick={() => setAll("overwrite", true)}>
                  Overwrite all
                </Button>
                <Button size="sm" variant="outline" className="h-7" data-testid="duplicate-modal-skip-all-button" onClick={() => setAll("skip", true)}>
                  Skip all
                </Button>
              </div>
            )}

            <div className="max-h-[45vh] overflow-y-auto -mx-1 px-1 space-y-2">
              {preview.rows.map((r) => (
                <div
                  key={r.index}
                  data-testid={`upload-row-${r.index}`}
                  className={`rounded-lg border p-3 text-sm ${
                    r.is_duplicate ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white dark:bg-slate-900"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileSpreadsheet className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-medium truncate">
                          {Object.values(r.key).filter(Boolean).join(" · ") || `Row ${r.index + 1}`}
                        </span>
                        {r.is_duplicate ? (
                          <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">DUPLICATE</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">NEW</Badge>
                        )}
                      </div>
                      {r.is_duplicate && r.existing && (
                        <div className="mt-1.5 grid sm:grid-cols-2 gap-2 text-xs">
                          <div className="text-slate-500">
                            <span className="uppercase tracking-wide text-[10px] text-slate-400">Existing</span>
                            <div className="font-mono-num">{columns.map((c) => `${c}: ${fmt(r.existing[c])}`).join("  ·  ")}</div>
                          </div>
                          <div className="text-blue-700">
                            <span className="uppercase tracking-wide text-[10px] text-blue-400">Uploaded</span>
                            <div className="font-mono-num">{columns.map((c) => `${c}: ${fmt(r.incoming[c])}`).join("  ·  ")}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="shrink-0">
                      {r.is_duplicate ? (
                        <div className="flex rounded-md overflow-hidden border border-slate-300">
                          <button
                            data-testid={`row-${r.index}-skip`}
                            onClick={() => setActions({ ...actions, [r.index]: "skip" })}
                            className={`px-3 py-1 text-xs ${actions[r.index] === "skip" ? "bg-slate-700 text-white" : "bg-white text-slate-600"}`}
                          >
                            Skip
                          </button>
                          <button
                            data-testid={`row-${r.index}-overwrite`}
                            onClick={() => setActions({ ...actions, [r.index]: "overwrite" })}
                            className={`px-3 py-1 text-xs ${actions[r.index] === "overwrite" ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`}
                          >
                            Overwrite
                          </button>
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Will add</Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => reset()}>Back</Button>
              <Button onClick={commit} disabled={loading} className="bg-[#78BE20] hover:bg-[#5fa016]" data-testid="duplicate-modal-confirm-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm import"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
